/**
 * SOLID Review
 * S — Strong. One use case: one tick of the lobby's self-heal poll — a round the lobby did not hear of.
 * O — Strong. The round trip runs it; a change of rule is the Domain's, of sequence the round trip's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientTableInput } from '../../domain/ports/schemas/ClientTable';
import type { ICommandLobbyActiveRound, ICommandNothing, IPayloadRoomRead } from '../../domain/ports/schemas/TableMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepTable } from './IConfigDepTable';

/** One tick of the lobby's self-heal poll (FE1's lobbyPollTick; `ClientJobs` every 2.5 s): a round the lobby missed. */
export class LobbyPollTickUseCase implements IUseCase<ICommandNothing, TPortOutcome>, TServerRoundTrip<IPropsClientTableInput, ICommandNothing, IPayloadRoomRead, TAnswered<ICommandLobbyActiveRound['activeRound']>> {
  constructor(private readonly deps: IConfigDepTable) {}

  execute(ctx: IContextRequest, command: ICommandNothing): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientTableInput, command: ICommandNothing) {
    return this.deps.decisions.planLobbyRead(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadRoomRead) {
    return this.deps.rounds.getActiveRound(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientTableInput, plan: IPayloadRoomRead, answered: TAnswered<ICommandLobbyActiveRound['activeRound']>) {
    const polled: ICommandLobbyActiveRound = { request: plan, activeRound: answered.value };
    return this.deps.decisions.lobbyActiveRound(ctx, input, polled);
  }
}
